import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FilterMatchMode, LazyLoadEvent, MessageService, SelectItem } from 'primeng/api';
import { UntypedFormControl, UntypedFormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { TaskCatalogueService } from './task-catalogue.service';
import { descriptionType, rateType, taskTypes } from 'src/app/utils/taskCatalogData';
import { helperService } from 'src/app/utils/helper';
import { taskCatalog } from 'src/app/utils/filterDropDownData';
import { UsersGridService } from '../users-grid/users-grid.service';

// Custom validator for HTML content text length
function htmlTextLengthValidator(maxLength: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    
    // Create a temporary element to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = control.value;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (textContent.length > maxLength) {
      return { maxTextLength: { requiredLength: maxLength, actualLength: textContent.length } };
    }
    
    return null;
  };
}

// Main component for Task Catalogue
@Component({
    selector: 'app-task-catalogue',
    templateUrl: './task-catalogue.component.html',
    styleUrls: ['./task-catalogue.component.scss'],
    standalone: false
})
export class TaskCatalogueComponent implements OnInit {

  // Table and UI state
  tasks: any[] = [];
  cols: any[] = [];
  taskCatDialog = false;
  viewOnly = false;
  newTask = false;
  title = '';
  loading = true;
  totalRecords = 0;
  first = 1;
  submitted = false;
  btnLoading = false;
  public lastTableLazyLoadEvent: any = null;

  taskCatSubscription:any;

  // Dropdown and config data
  defaultTime: Date = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
  readyStateList = taskCatalog.readyStateList;
  reqEstimateList = taskCatalog.reqEstimateList;
  stateRelatedList = taskCatalog.stateRelatedList;
  billableList = taskCatalog.billableList;
  descriptionTypesList = taskCatalog.descriptionTypesList;
  rateTypesList = taskCatalog.rateTypesList;
  descriptionTypes = descriptionType;
  rateTypes = rateType;
  taskGridTypes = taskTypes;
  practiceAreaCodes: any[] = [];

  // Disbursement dropdowns
  disbursementTaskOptions: any[] = [];
  originalDisbursementTaskOptions: any[] = [];
  disbursementTaskTypes: any[] = [];
  private disbursementTaskSubscription: any = null;

  // Form
  taskForm: UntypedFormGroup;
  defaultTaskForm: any = {};

  constructor(
    private userService: UsersGridService,
    private taskCatService: TaskCatalogueService,
    private readonly messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private filterService: helperService
  ) {
    // Initialize form group
    this.taskForm = new UntypedFormGroup({
      qbTaskId: new UntypedFormControl(''),
      id: new UntypedFormControl(''),
      taskName: new UntypedFormControl(''),
      taskTypeCode: new UntypedFormControl(''),
      qbDescription: new UntypedFormControl(''),
      descriptionTypeCode: new UntypedFormControl('', [Validators.required]),
      rate: new UntypedFormControl('', [Validators.required]),
      rateTypeCode: new UntypedFormControl('', [Validators.required]),
      timeRangeMin: new UntypedFormControl(''),
      timeRangeMax: new UntypedFormControl(''),
      enforceMax: new UntypedFormControl(''),
      timeEstimate: new UntypedFormControl(''),
      taskClass: new UntypedFormControl(''),
      billable: new UntypedFormControl('', [Validators.required]),
      reqEstimate: new UntypedFormControl(''),
      practiceAreaCode: new UntypedFormControl('', [Validators.required]),
      stateRelated: new UntypedFormControl(''),
      countryRelated: new UntypedFormControl(''),
      quantityRelated: new UntypedFormControl(''),
      quantityType: new UntypedFormControl(''),
      projectOnly: new UntypedFormControl(''),
      timeRecords: new UntypedFormControl('Yes'),
      readyStatus: new UntypedFormControl(''),
      disbursementRelated: new UntypedFormControl('No'),
      disbursementAssociatedTasks: new UntypedFormControl([]),
      disbursementTaskType: new UntypedFormControl(''),
      nonBillableReason: new UntypedFormControl('', [Validators.required]),
      taskInstructions: new UntypedFormControl('', [htmlTextLengthValidator(10000)]),
      entityRelated: new UntypedFormControl('No')
    });
  }

  ngOnInit(): void {
    this.initTableCols();
    this.initFormListeners();
    this.defaultTaskForm = this.taskForm.value;
    this.getTkPracticeArea();
    this.loadDisbursementTasks();
    this.loadDisbursementTypes();
  }

  // Initialize table columns
  private initTableCols() {
    this.cols = [
      { field: 'taskName', header: 'Task Name' },
      { field: 'taskFullName', header: 'Task Full Name' },
      { field: 'taskTypeCode', header: 'Task Type Code' },
      { field: 'qbTaskId', header: 'QuickBook ID' },
      { field: 'qbDescription', header: 'Description' },
      { field: 'disbursementRelated', header: 'Disbursement Related' },
      { field: 'descriptionTypeCode', header: 'Description Type' },
      { field: 'rate', header: 'Rate' },
      { field: 'rateTypeCode', header: 'Rate Type' },
      { field: 'timeEstimate', header: 'Time Estimate' },
      { field: 'taskClass', header: 'Class' },
      { field: 'billable', header: 'Billable' },
      { field: 'reqEstimate', header: 'Request Estimate' },
      { field: 'practiceAreaCode', header: 'Practice Area Code' },
      { field: 'stateRelated', header: 'State Related' },
      { field: 'readyStatus', header: 'Ready Status' },
      { field: 'entityRelated', header: 'Entity Related' }
    ];
  }

  // Form listeners for dynamic validation
  private initFormListeners() {
    // Quantity Related logic
    this.taskForm.get('quantityRelated')?.valueChanges.subscribe((isqtyRel: any) => {
      const qTcontrol = this.taskForm.get('quantityType');
      const taskTypeControl = this.taskForm.get('taskTypeCode');
      if (isqtyRel === 'Yes' && taskTypeControl?.value !== "Disbursements") {
        qTcontrol?.setValidators([Validators.required]);
      } else {
        qTcontrol?.clearValidators();
      }
      qTcontrol?.updateValueAndValidity();
    });

    // Task Type logic
    this.taskForm.get('taskTypeCode')?.valueChanges.subscribe((isqtyRel: any) => {
      const disbursementTypeControl = this.taskForm.get('disbursementTaskType');
      const disbursementRelatedControl = this.taskForm.get('disbursementRelated');
      const controlsToClear = [
        'timeRangeMin', 'timeRangeMax', 'enforceMax', 'timeEstimate',
        'stateRelated', 'countryRelated', 'quantityRelated', 'quantityType',"disbursementRelated"
      ];
      if (isqtyRel === 'Disbursements') {
        disbursementTypeControl?.setValidators([Validators.required]);
        controlsToClear.forEach(key => {
          const ctrl = this.taskForm.get(key);
          ctrl?.clearValidators();
          ctrl?.setValue(null);
          ctrl?.updateValueAndValidity();
        });
        this.taskForm.get("timeRecords")?.setValue("No");
        this.taskForm.get("timeRecords")?.disable();
        this.taskForm.get("disbursementRelated")?.setValue(null);
      } else {
        disbursementRelatedControl?.setValidators([Validators.required]);
        controlsToClear.forEach(key => {
          const ctrl = this.taskForm.get(key);
          ctrl?.setValidators([Validators.required]);
          ctrl?.updateValueAndValidity();
        });
        disbursementTypeControl?.clearValidators();
        disbursementTypeControl?.setValue(null);
        disbursementTypeControl?.updateValueAndValidity();
        this.taskForm.get("timeRecords")?.setValue("Yes");
        this.taskForm.get("timeRecords")?.enable();
      }
    });

    // Disbursement Related logic
    this.taskForm.get('disbursementRelated')?.valueChanges.subscribe((isDisbRel: any) => {
      const disbTaskControl = this.taskForm.get('disbursementAssociatedTasks');
      if (isDisbRel === 'Yes' && this.taskForm.get('taskTypeCode')?.value !== "Disbursements") {
        disbTaskControl?.enable();
        disbTaskControl?.setValidators([Validators.required]);
      } else {
        disbTaskControl?.setValue([]);
        disbTaskControl?.disable();
        disbTaskControl?.clearValidators();
      }
      disbTaskControl?.updateValueAndValidity();
    });

    this.taskForm.get('billable')?.valueChanges.subscribe((isBillable: any) => {
      const nonBillableControl = this.taskForm.get('nonBillableReason');
      if (isBillable === 'Yes') {
        nonBillableControl?.clearValidators();
      } else {
        nonBillableControl?.setValidators([Validators.required]);
      }
      nonBillableControl?.updateValueAndValidity();
    });
  }

  // Fetch practice areas
  getTkPracticeArea() {
    this.userService.getPracticeArea().subscribe(res => {
      this.practiceAreaCodes = res.data;
    });
  }

  // Fetch disbursement types
  loadDisbursementTypes() {
    this.taskCatService.fetchdisbursementtypes().subscribe((res: any) => {
      this.disbursementTaskTypes = res.data || [];
    }, (error: any) => {
      console.error('Error fetching disbursement types:', error);
    });
  }

   // Fetch disbursement tasks for dropdown
  loadDisbursementTasks() {
    // Unsubscribe previous subscription if exists
    if (this.disbursementTaskSubscription) {
      this.disbursementTaskSubscription.unsubscribe();
    }
    this.disbursementTaskSubscription = this.taskCatService.fetchDisbursementTasks().subscribe({
      next: (res: any) => {
        const tasks = Array.isArray(res.data) ? res.data : [];
        this.disbursementTaskOptions = tasks
          .filter((task: any) => task.taskTypeCode === 'Disbursements')
          .map((task: any) => ({ label: task.taskName, value: task.id }));
        this.originalDisbursementTaskOptions = [...this.disbursementTaskOptions];
      },
      error: (err: any) => {
        console.error('Error fetching disbursement tasks:', err);
        this.disbursementTaskOptions = [];
        this.originalDisbursementTaskOptions = [];
      }
    });
  }
  // Load main task data for table
  loadTaskData(event: LazyLoadEvent) {
    this.lastTableLazyLoadEvent = event;
    this.loading = true;
    event.first = event.first || 1;
    event.rows = event.rows || 10;
    let payload=this.filterService.generateEventPayload(event);
    if (this.taskCatSubscription) {
      this.taskCatSubscription.unsubscribe();
    }
    this.taskCatSubscription = this.taskCatService.getCatList(Math.floor(event.first / event.rows + 1), event.rows,payload).subscribe(res => {

      let list = res.data;
      list.forEach((obj: any) => {
        obj['timeEstimate'] = this.filterService.convertTimeNumToStr(obj.timeEstimate);
        obj["disbursementAssociatedTasks"] = obj.disbursementTasks
          ? Array.from(new Set(obj.disbursementTasks.map((task: any) => task.disbursementTaskId)))
          : [];
      });
      this.tasks = list;
      this.loading = false;
      this.totalRecords = res.customValues.totalNumberOfRecords;
    });
  }

  // Dialog actions
  addTask(): void {
    this.taskCatDialog = true;
    this.viewOnly = false;
    this.newTask = true;
    this.title = "Add Task Catalog";
  }

  closeDialog(): void {
    this.taskCatDialog = false;
    this.submitted = false;
    // Reset form to default values, but don't override existing values when editing
    if (this.newTask) {
      this.taskForm.reset(this.defaultTaskForm);
    } else {
      // For editing, just reset to clean state without changing values
      this.taskForm.markAsUntouched();
      this.taskForm.markAsPristine();
    }
    this.taskForm.enable();
    this.loadTaskData(this.lastTableLazyLoadEvent);
    this.loadDisbursementTasks();
    this.cdr.detectChanges();
  }

  // Edit Task
  editTask(task: any): void {
    task.timeEstimate = this.filterService.convertTimeToDate(this.filterService.convertStrToNum(task.timeEstimate));
    task.timeRangeMin = this.filterService.convertTimeToDate(task.timeRangeMin);
    task.timeRangeMax = this.filterService.convertTimeToDate(task.timeRangeMax);
    this.disbursementTaskOptions=this.originalDisbursementTaskOptions?.filter((taskObj:any)=>taskObj.value!== task.id);
    this.taskForm.patchValue(task);
    this.taskForm.get('projectOnly')?.setValue(task.qbTaskId ? "No" : "Yes");
    if (task.qbTaskId) this.taskForm.get('projectOnly')?.disable();
    this.taskCatDialog = true;
    this.viewOnly = false;
    this.newTask = false;
    this.title = "Edit Task Catalog";
    this.onRateTypeChange();
    if (this.taskForm.get('taskTypeCode')?.value === "Disbursements") {
      this.taskForm.get('disbursementRelated')?.setValue("No");
      this.taskForm.get("timeRecords")?.setValue("No");
      this.taskForm.get("timeRecords")?.disable();
      if (!this.taskForm.get('rate')?.value) {
        this.taskForm.get('rate')?.setValue(0);
      }
    } else {
      if (this.taskForm.get('disbursementRelated')?.value === "Yes") {
        this.taskForm.get('disbursementAssociatedTasks')?.enable();
      } else {
        const ctrl = this.taskForm.get('disbursementAssociatedTasks');
        ctrl?.disable();
        ctrl?.clearValidators();
        ctrl?.updateValueAndValidity();
      }
    }

    if(task.billable==='Yes'){
      this.taskForm.get('nonBillableReason')?.clearValidators();
    } else{
      this.taskForm.get('nonBillableReason')?.setValidators([Validators.required]);
    }

    if (this.taskForm.get('quantityRelated')?.value === 'Yes' && this.taskForm.get('taskTypeCode')?.value !== "Disbursements") {
      this.taskForm.get('quantityType')?.setValidators([Validators.required]);
    } else {
      this.taskForm.get('quantityType')?.clearValidators();
    }
    this.taskForm.get('quantityType')?.updateValueAndValidity();

    this.taskForm.get('nonBillableReason')?.updateValueAndValidity();
  }

  // View Task
  viewTask(task: any): void {
    task.timeEstimate = this.filterService.convertTimeToDate(this.filterService.convertStrToNum(task.timeEstimate));
    task.timeRangeMin = this.filterService.convertTimeToDate(task.timeRangeMin);
    task.timeRangeMax = this.filterService.convertTimeToDate(task.timeRangeMax);
    this.taskForm.patchValue(task);
    this.taskForm.disable();
    this.taskCatDialog = true;
    this.viewOnly = true;
    this.newTask = false;
    this.title = "View Task Catalog";
  }

  // Save Task
  saveTask() {
    if (this.btnLoading) return;
    this.submitted = true;
    this.messageService.clear();
    if (this.taskForm.valid) {
      this.btnLoading = true;
      this.taskForm.disable();
      try {
        let x = this.taskForm.getRawValue();
        x.timeEstimate = this.filterService.convertDateToTime(x.timeEstimate);
        x.timeRangeMin = this.filterService.convertDateToTime(x.timeRangeMin);
        x.timeRangeMax = this.filterService.convertDateToTime(x.timeRangeMax);

        if (x.taskTypeCode === "Disbursements") {
          // Only keep relevant keys for Disbursements
          const allowedKeys = [
            'qbTaskId', 'id', 'taskName', 'taskTypeCode', 'qbDescription',
            'descriptionTypeCode', 'rate', 'rateTypeCode', 'taskClass', 'billable',
            'reqEstimate', 'practiceAreaCode', 'projectOnly', 'readyStatus',
            'disbursementTaskType', 'timeRecords', 'nonBillableReason', 'taskInstructions', 'entityRelated'
          ];
          Object.keys(x).forEach(key => {
            if (!allowedKeys.includes(key)) x[key] = null;
          });
        } else {
          // Remove disbursement-only keys for non-disbursement types
          x.disbursementTaskType = "";
        }

        this.taskCatService.saveCatalog(x, this.taskForm.get('id')?.value).subscribe(
          res => {
            this.btnLoading = false;
            if (res.message) {
              this.viewOnly = true;
              this.messageService.add({ severity: 'success', detail: 'Task ' + this.taskForm.get('taskName')?.value + ' details have been saved successfully' });
            } else {
              this.messageService.add({ severity: 'error', detail: res.message });
            }
          },
          error => {
            this.btnLoading = false;
            console.log(error);
            this.messageService.add({ severity: 'error', detail: error.message });
          }
        );
      } catch (error: any) {
        this.btnLoading = false;
        console.log(error);
        this.messageService.add({ severity: 'error', detail: error.message });
      }
    } else {
      // Find and print which controls are invalid
      const invalidControls = Object.keys(this.taskForm.controls)
        .filter(key => this.taskForm.controls[key].invalid)
        .map(key => key);
      console.log('Invalid form controls:', invalidControls);
      this.messageService.add({
        severity: 'error',
        detail: `Please fill all mandatory fields correctly. Invalid: ${invalidControls.join(', ')}`
      });
    }
  }

  // Rate Type change handler
  onRateTypeChange() {
    const rateType = this.taskForm.get('rateTypeCode')?.value;
    const enforceMaxCtrl = this.taskForm.get('enforceMax');
    const timeRangeMaxCtrl = this.taskForm.get('timeRangeMax');
    if (rateType === "Fixed Rate") {
      enforceMaxCtrl?.enable();
      if (this.taskForm.get('taskTypeCode')?.value !== "Disbursements") {
        enforceMaxCtrl?.setValidators([Validators.required]);
        enforceMaxCtrl?.updateValueAndValidity();
      }
      this.onEnforceMaxChange();
    } else if (rateType === "Hourly") {
      enforceMaxCtrl?.disable();
      enforceMaxCtrl?.clearValidators();
      enforceMaxCtrl?.updateValueAndValidity();
      timeRangeMaxCtrl?.disable();
      timeRangeMaxCtrl?.clearValidators();
      timeRangeMaxCtrl?.updateValueAndValidity();
    }
  }

  // Enforce Max change handler
  onEnforceMaxChange() {
    const enforceMax = this.taskForm.get('enforceMax')?.value;
    const timeRangeMaxCtrl = this.taskForm.get('timeRangeMax');
    if (enforceMax === "Yes") {
      timeRangeMaxCtrl?.enable();
      if (this.taskForm.get('taskTypeCode')?.value !== "Disbursements") {
        timeRangeMaxCtrl?.setValidators([Validators.required]);
        timeRangeMaxCtrl?.updateValueAndValidity();
      }
    } else {
      timeRangeMaxCtrl?.clearValidators();
      timeRangeMaxCtrl?.updateValueAndValidity();
    }
  }

  // Form control getters for template usage
  get taskName() { return this.taskForm.get('taskName'); }
  get taskTypeCode() { return this.taskForm.get('taskTypeCode'); }
  get description() { return this.taskForm.get('qbDescription'); }
  get descriptionTypeCode() { return this.taskForm.get('descriptionTypeCode'); }
  get rate() { return this.taskForm.get('rate'); }
  get rateTypeCode() { return this.taskForm.get('rateTypeCode'); }
  get timeRangeMin() { return this.taskForm.get('timeRangeMin'); }
  get timeRangeMax() { return this.taskForm.get('timeRangeMax'); }
  get enforceMax() { return this.taskForm.get('enforceMax'); }
  get timeEstimate() { return this.taskForm.get('timeEstimate'); }
  get taskClass() { return this.taskForm.get('taskClass'); }
  get billable() { return this.taskForm.get('billable'); }
  get reqEstimate() { return this.taskForm.get('reqEstimate'); }
  get practiceAreaCode() { return this.taskForm.get('practiceAreaCode'); }
  get stateRelated() { return this.taskForm.get('stateRelated'); }
  get countryRelated() { return this.taskForm.get('countryRelated'); }
  get quantityRelated() { return this.taskForm.get('quantityRelated'); }
  get quantityType() { return this.taskForm.get('quantityType'); }
  get projectOnly() { return this.taskForm.get('projectOnly'); }
  get timeRecords() { return this.taskForm.get('timeRecords'); }
  get readyStatus() { return this.taskForm.get('readyStatus'); }
  get disbursementAssociatedTasks() { return this.taskForm.get('disbursementAssociatedTasks'); }
  get disbursementTaskType() { return this.taskForm.get('disbursementTaskType'); }
  get nonBillableReason() { return this.taskForm.get('nonBillableReason'); }
  get taskInstructions() { return this.taskForm.get('taskInstructions'); }
  get entityRelated() { return this.taskForm.get('entityRelated'); }

  // Get text-only character count (excluding HTML tags)
  getTaskInstructionsTextLength(): number {
    const htmlContent = this.taskInstructions?.value || '';
    if (!htmlContent) return 0;
    
    // Create a temporary element to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.length;
  }

}